import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  BrainCircuit,
  CheckCircle2,
  Clock,
  Database,
  FileText,
  HardDrive,
  Headset,
  Megaphone,
  Package,
  Server,
  Settings as SettingsIcon,
  ShieldAlert,
  ShoppingBag,
  Store,
  Tags,
  TrendingUp,
  Truck,
  Users,
  Wallet,
  X,
  Search,
  ChevronRight,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Spinner } from '@terra/ui';
import { formatPriceShort } from '@terra/utils';

import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';

// Utilities
/** Indian dashboard figure: ₹2,435 · ₹1.25 L · ₹2.5 Cr (never "K"). */
const compactInr = (p: number) => formatPriceShort(p || 0);

const formatPaise = (p: number) =>
  `₹${((p || 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.5, ease: [0.16, 1, 0.3, 1] as const },
  }),
};

function Skeleton({ className }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-slate-200/60 ${className}`} />;
}

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const [showRevenueModal, setShowRevenueModal] = useState(false);

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => api.vendors.dashboardStats(),
  });

  const { data: dashToday, isLoading: dashTodayLoading } = useQuery({
    queryKey: ['analytics-dashboard-today'],
    queryFn: () => api.analytics.dashboard({ period: 'DAY' }),
    retry: false,
  });

  const { data: dash, isLoading: dashLoading } = useQuery({
    queryKey: ['analytics-dashboard'],
    queryFn: () => api.analytics.dashboard(),
    retry: false,
  });

  const { data: breakdown } = useQuery({
    queryKey: ['platform-product-breakdown-summary'],
    queryFn: () => api.analytics.platformProductBreakdown('all'),
  });

  const { data: systemHealth } = useQuery({
    queryKey: ['system-health'],
    queryFn: () => api.analytics.systemHealth(),
    refetchInterval: 30000, // refresh every 30s
  });

  const { data: liveActivity } = useQuery({
    queryKey: ['live-activity'],
    queryFn: () => api.analytics.liveActivity(),
    refetchInterval: 15000, // refresh every 15s
  });

  const { data: snapshots } = useQuery({
    queryKey: ['analytics-snapshots-daily'],
    queryFn: () => api.analytics.listSnapshots({ domain: 'PLATFORM', period: 'DAY', limit: 7 }),
  });

  const loading = statsLoading || dashLoading;

  // Revenue Data (Area Chart)
  const revenueData =
    snapshots && snapshots.length > 0
      ? [...snapshots].reverse().map((s) => ({
          name: new Date(s.periodStart).toLocaleDateString('en-US', { weekday: 'short' }),
          gmv: s.metrics.gmvPaise || 0,
          rev: s.metrics.marketplaceSalesPaise || 0,
        }))
      : [
          { name: 'Mon', gmv: 0, rev: 0 },
          { name: 'Tue', gmv: 0, rev: 0 },
          { name: 'Wed', gmv: 0, rev: 0 },
          { name: 'Thu', gmv: 0, rev: 0 },
          { name: 'Fri', gmv: 0, rev: 0 },
          { name: 'Sat', gmv: 0, rev: 0 },
          { name: 'Sun', gmv: 0, rev: 0 },
        ];

  const channels = [
    { label: 'Marketplace', value: dash?.platform?.marketplaceSalesPaise ?? 0, color: '#6C63FF' },
    { label: 'POS', value: dash?.platform?.posSalesPaise ?? 0, color: '#10B981' },
    { label: 'B2B', value: dash?.platform?.b2bSalesPaise ?? 0, color: '#3B82F6' },
  ];
  const channelTotal = channels.reduce((s, c) => s + c.value, 0);
  const slaPct = (dash?.ops?.slaBps ?? 0) / 100;

  // Workspaces
  const workspaces = [
    {
      to: '/sellers',
      label: 'Vendors',
      desc: 'Manage vendor accounts',
      Icon: Store,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      to: '/products',
      label: 'Catalog',
      desc: 'Moderate products',
      Icon: Package,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
    },
    {
      to: '/orders/all',
      label: 'Orders',
      desc: 'Fulfillment & tracking',
      Icon: ShoppingBag,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
    },
    {
      to: '/crm',
      label: 'Customers',
      desc: 'User management',
      Icon: Users,
      color: 'text-orange-600',
      bg: 'bg-orange-50',
    },
    {
      to: '/marketing',
      label: 'Marketing',
      desc: 'Campaigns & coupons',
      Icon: Megaphone,
      color: 'text-pink-600',
      bg: 'bg-pink-50',
    },
    {
      to: '/analytics',
      label: 'Reports',
      desc: 'Business intelligence',
      Icon: BarChart3,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50',
    },
    {
      to: '/support',
      label: 'Support',
      desc: 'Tickets & resolutions',
      Icon: Headset,
      color: 'text-rose-600',
      bg: 'bg-rose-50',
    },
    {
      to: '/payouts',
      label: 'Finance',
      desc: 'Settlements & payouts',
      Icon: Wallet,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
    },
  ];

  return (
    <div className="space-y-6 pb-12 font-sans text-slate-900">
      {/* 1. EXECUTIVE COMMAND CENTER (HERO) */}
      <motion.div
        custom={0}
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="relative overflow-hidden rounded-[32px] bg-slate-900 p-8 text-white shadow-2xl"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#6C63FF]/20 to-transparent opacity-50" />
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#6C63FF] opacity-20 blur-3xl" />

        <div className="relative z-10">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">
                Welcome back, {user?.name?.split(' ')[0] || 'Admin'}
              </h1>
              <p className="mt-1 text-slate-400">
                {"Here is what's happening in your marketplace today."}
              </p>
            </div>
            <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/10 px-5 py-3 backdrop-blur-md">
              <span className="relative flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
              </span>
              <span className="text-sm font-semibold uppercase tracking-wide text-emerald-400">
                All Systems Operational
              </span>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-6 lg:grid-cols-4">
            <button
              type="button"
              onClick={() => setShowRevenueModal(true)}
              className="group relative flex flex-col justify-between border-l-2 border-[#6C63FF] pl-4 text-left transition-all hover:translate-x-1"
              title="Click to view product revenue breakdown"
            >
              <div>
                <div className="flex items-center gap-1">
                  <p className="text-sm font-medium text-slate-400 group-hover:text-white">
                    Gross Revenue (Today)
                  </p>
                  <ChevronRight className="h-4 w-4 text-slate-500 transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
                </div>
                <h2 className="mt-1 text-3xl font-bold tracking-tight text-emerald-400">
                  {loading ? (
                    <Skeleton className="h-8 w-24 bg-white/20" />
                  ) : (
                    compactInr(dashToday?.platform?.gmvPaise || breakdown?.todayRevenuePaise || 0)
                  )}
                </h2>
              </div>
              <p className="mt-1.5 text-xs text-slate-400">
                Total:{' '}
                <span className="font-semibold text-slate-200">
                  {compactInr(dash?.platform?.gmvPaise || breakdown?.totalRevenuePaise || 0)}
                </span>
              </p>
            </button>

            <button
              type="button"
              onClick={() => setShowRevenueModal(true)}
              className="group relative flex flex-col justify-between border-l-2 border-emerald-500 pl-4 text-left transition-all hover:translate-x-1"
              title="Click to view product revenue breakdown"
            >
              <div>
                <div className="flex items-center gap-1">
                  <p className="text-sm font-medium text-slate-400 group-hover:text-white">
                    Total Orders
                  </p>
                  <ChevronRight className="h-4 w-4 text-slate-500 transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
                </div>
                <h2 className="mt-1 text-3xl font-bold tracking-tight">
                  {loading ? (
                    <Skeleton className="h-8 w-16 bg-white/20" />
                  ) : (
                    dash?.platform?.orderCount || breakdown?.orderCount || 0
                  )}
                </h2>
              </div>
              <p className="mt-1.5 text-xs text-slate-400">Click for details</p>
            </button>

            <Link
              to="/sellers"
              className="group relative flex flex-col justify-between border-l-2 border-amber-500 pl-4 text-left transition-all hover:translate-x-1"
            >
              <div>
                <div className="flex items-center gap-1">
                  <p className="text-sm font-medium text-slate-400 group-hover:text-white">
                    Active Vendors
                  </p>
                  <ChevronRight className="h-4 w-4 text-slate-500 transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
                </div>
                <h2 className="mt-1 text-3xl font-bold tracking-tight">
                  {loading ? (
                    <Skeleton className="h-8 w-16 bg-white/20" />
                  ) : (
                    stats?.activeVendors || 0
                  )}
                </h2>
              </div>
              <p className="mt-1.5 text-xs text-slate-400">Manage sellers</p>
            </Link>

            <div className="border-l-2 border-pink-500 pl-4">
              <p className="text-sm font-medium text-slate-400">Platform Health</p>
              <h2 className="mt-1 text-3xl font-bold tracking-tight">
                {dash?.ops?.slaBps ? (dash.ops.slaBps / 100).toFixed(1) : 100}%
              </h2>
              <p className="mt-1.5 text-xs text-slate-400">SLA Fulfillment</p>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* LEFT COLUMN: Main Dashboards (Takes up 2 cols on XL) */}
        <div className="space-y-6 xl:col-span-2">
          {/* 2. ACTION REQUIRED CENTER */}
          <motion.div
            custom={1}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="rounded-3xl bg-white p-7 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)]"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-red-50 text-red-600">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Action Required</h2>
                <p className="text-sm text-slate-500">Tasks needing immediate admin attention</p>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
              {[
                {
                  label: 'Vendor Approvals',
                  value: stats?.pendingVendors,
                  to: '/sellers',
                  icon: Store,
                  color: 'text-amber-600',
                  bg: 'bg-amber-50',
                  border: 'border-amber-100',
                },
                {
                  label: 'Product Reviews',
                  value: stats?.pendingProducts,
                  to: '/products',
                  icon: Package,
                  color: 'text-rose-600',
                  bg: 'bg-rose-50',
                  border: 'border-rose-100',
                },
                {
                  label: 'Pending Payouts',
                  value: stats?.pendingPayouts,
                  to: '/payouts',
                  icon: Wallet,
                  color: 'text-blue-600',
                  bg: 'bg-blue-50',
                  border: 'border-blue-100',
                },
                {
                  label: "Today's Orders",
                  value: stats?.todayOrders,
                  to: '/orders/all',
                  icon: ShoppingBag,
                  color: 'text-purple-600',
                  bg: 'bg-purple-50',
                  border: 'border-purple-100',
                },
              ].map((item, idx) => (
                <Link
                  key={idx}
                  to={item.to}
                  className={`group relative overflow-hidden rounded-2xl border ${item.border} p-4 transition-all hover:-translate-y-1 hover:shadow-lg`}
                >
                  <div className={`mb-3 inline-block rounded-lg ${item.bg} p-2 ${item.color}`}>
                    <item.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-2xl font-black">
                    {loading ? <Skeleton className="h-6 w-10" /> : item.value || 0}
                  </h3>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500 group-hover:text-slate-900">
                    {item.label}
                  </p>
                </Link>
              ))}
            </div>
          </motion.div>

          {/* 3. REVENUE INTELLIGENCE HUB */}
          <motion.div
            custom={2}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="rounded-3xl bg-white p-7 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)]"
          >
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold">Revenue Intelligence</h2>
                <p className="text-sm text-slate-500">GMV and Net Revenue trends</p>
              </div>
              <select className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold outline-none focus:border-[#6C63FF] focus:ring-1 focus:ring-[#6C63FF]">
                <option>Last 7 days</option>
                <option>Last 30 days</option>
                <option>This Quarter</option>
              </select>
            </div>

            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorGmv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6C63FF" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6C63FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    tickFormatter={(val: number) => compactInr(val)}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: '16px',
                      border: 'none',
                      boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)',
                    }}
                    itemStyle={{ fontWeight: 600 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="gmv"
                    stroke="#6C63FF"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#colorGmv)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-4 border-t border-slate-100 pt-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Net Platform Revenue
                </p>
                <h4 className="mt-1 text-xl font-bold text-slate-900">
                  {compactInr(dash?.finance?.netPaise || 0)}
                </h4>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Platform Cost (COGS)
                </p>
                <h4 className="mt-1 text-xl font-bold text-slate-500">
                  {compactInr(dash?.finance?.cogsPaise || 0)}
                </h4>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Operating Expenses
                </p>
                <h4 className="mt-1 text-xl font-bold text-red-500">
                  {compactInr(dash?.finance?.operatingExpensePaise || 0)}
                </h4>
              </div>
            </div>
          </motion.div>

          {/* 5. OPERATIONS CONTROL CENTER */}
          <motion.div
            custom={3}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="rounded-3xl bg-white p-7 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)]"
          >
            <div className="mb-6">
              <h2 className="text-lg font-bold">Operations Control Center</h2>
              <p className="text-sm text-slate-500">Manage all aspects of your marketplace</p>
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {workspaces.map((ws) => (
                <Link
                  key={ws.label}
                  to={ws.to}
                  className="group relative flex flex-col items-center rounded-2xl border border-slate-100 bg-slate-50 p-5 text-center transition-all hover:-translate-y-1 hover:border-transparent hover:bg-white hover:shadow-[0_10px_30px_-10px_rgba(0,0,0,0.1)]"
                >
                  <div
                    className={`mb-3 grid h-12 w-12 place-items-center rounded-xl ${ws.bg} ${ws.color} transition-transform group-hover:scale-110 group-hover:shadow-md`}
                  >
                    <ws.Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-900">{ws.label}</h3>
                  <p className="mt-1 text-[11px] font-medium text-slate-500">{ws.desc}</p>
                </Link>
              ))}
            </div>
          </motion.div>
        </div>

        {/* RIGHT COLUMN: Sidebar panels */}
        <div className="space-y-6">
          {/* 4. MARKETPLACE PERFORMANCE */}
          <motion.div
            custom={4}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="rounded-3xl bg-white p-7 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)]"
          >
            <h2 className="text-lg font-bold">Sales by Channel</h2>
            <div className="relative mx-auto mt-6 h-48 w-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip
                    cursor={{ fill: 'transparent' }}
                    contentStyle={{
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    }}
                    formatter={(val: number) => compactInr(val)}
                  />
                  <Pie
                    data={
                      channelTotal > 0 ? channels : [{ label: 'none', value: 1, color: '#F8FAFC' }]
                    }
                    dataKey="value"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    innerRadius={65}
                    outerRadius={90}
                    paddingAngle={5}
                    stroke="none"
                  >
                    {(channelTotal > 0
                      ? channels
                      : [{ label: 'none', value: 1, color: '#F8FAFC' }]
                    ).map((c) => (
                      <Cell key={c.label} fill={c.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xs font-bold uppercase text-slate-400">Total GMV</span>
                <span className="text-xl font-extrabold">
                  {loading ? (
                    <Skeleton className="h-6 w-16" />
                  ) : (
                    compactInr(dash?.platform?.gmvPaise || 0)
                  )}
                </span>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              {channels.map((c) => {
                const pct = channelTotal > 0 ? (c.value / channelTotal) * 100 : 0;
                return (
                  <div key={c.label}>
                    <div className="flex justify-between text-sm font-semibold">
                      <span className="flex items-center gap-2">
                        <div
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: c.color }}
                        />
                        {c.label}
                      </span>
                      <span>{compactInr(c.value)}</span>
                    </div>
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{ width: `${pct}%`, backgroundColor: c.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* 6. LIVE MARKETPLACE ACTIVITY */}
          <motion.div
            custom={5}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="rounded-3xl bg-white p-7 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)]"
          >
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-bold">Live Activity Stream</h2>
              <span className="flex h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
            </div>
            <div className="space-y-4">
              {liveActivity ? (
                liveActivity.length > 0 ? (
                  liveActivity.map((act) => (
                    <div key={act.id} className="flex gap-4">
                      <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-50 text-slate-600">
                        <Activity className="h-4 w-4" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold">{act.title}</h4>
                        <p className="text-xs font-medium text-slate-500">{act.desc}</p>
                        <span className="mt-1 block text-[10px] font-bold uppercase text-slate-400">
                          {new Date(act.time).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">No recent activity.</p>
                )
              ) : (
                <Skeleton className="h-48 w-full" />
              )}
            </div>
            <Link
              to="/setup/audit-logs"
              className="mt-5 block w-full rounded-xl bg-slate-50 py-2.5 text-center text-sm font-bold text-slate-600 transition-colors hover:bg-slate-100"
            >
              View Audit Logs
            </Link>
          </motion.div>

          {/* 8. SYSTEM HEALTH CENTER */}
          <motion.div
            custom={6}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="rounded-3xl bg-slate-900 p-7 text-white shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)]"
          >
            <h2 className="text-lg font-bold">Infrastructure Status</h2>
            <div className="mt-5 grid grid-cols-2 gap-4">
              {[
                {
                  label: 'API Server',
                  stat: systemHealth?.apiServer || '...',
                  icon: Server,
                  color: systemHealth?.apiServer === 'OK' ? 'text-emerald-400' : 'text-amber-400',
                },
                {
                  label: 'Database',
                  stat: systemHealth?.database || '...',
                  icon: Database,
                  color: systemHealth?.database === 'Failed' ? 'text-red-400' : 'text-emerald-400',
                },
                {
                  label: 'Redis Cache',
                  stat: systemHealth?.redisCache || '...',
                  icon: HardDrive,
                  color: systemHealth?.redisCache === 'OK' ? 'text-emerald-400' : 'text-red-400',
                },
                {
                  label: 'Worker Queue',
                  stat: systemHealth?.workerQueue || '...',
                  icon: Activity,
                  color: 'text-amber-400',
                },
              ].map((sys, i) => (
                <div key={i} className="rounded-2xl border border-white/5 bg-white/10 p-4">
                  <sys.icon className={`mb-2 h-5 w-5 ${sys.color}`} />
                  <p className="text-[11px] font-medium text-slate-400">{sys.label}</p>
                  <h4 className="text-sm font-bold">{sys.stat}</h4>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <RevenueProductBreakdownModal
        isOpen={showRevenueModal}
        onClose={() => setShowRevenueModal(false)}
      />
    </div>
  );
}

function RevenueProductBreakdownModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [range, setRange] = useState<'today' | '7d' | '30d' | 'all'>('today');
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['platform-product-breakdown', range],
    queryFn: () => api.analytics.platformProductBreakdown(range),
    enabled: isOpen,
  });

  if (!isOpen) return null;

  const filteredProducts = (data?.products ?? []).filter(
    (p) =>
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.sellerName.toLowerCase().includes(search.toLowerCase()),
  );

  const totalRev = data?.totalRevenuePaise ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative flex max-h-[85vh] w-full max-w-3xl flex-col rounded-3xl bg-white p-6 shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Product Revenue Breakdown</h2>
            <p className="text-xs font-medium text-slate-500">
              Detailed revenue generated by each product across sellers
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full bg-slate-100 p-2 text-slate-500 hover:bg-slate-200 hover:text-slate-900"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Range Tabs & Search */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-2 rounded-xl bg-slate-100 p-1">
            {(['today', '7d', '30d', 'all'] as const).map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                  range === r
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                {r === 'today'
                  ? 'Today'
                  : r === '7d'
                    ? '7 Days'
                    : r === '30d'
                      ? '30 Days'
                      : 'All Time'}
              </button>
            ))}
          </div>

          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search product or seller..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-xs font-medium outline-none focus:border-[#6C63FF] focus:bg-white"
            />
          </div>
        </div>

        {/* Summary Stats */}
        <div className="mt-4 grid grid-cols-3 gap-3 rounded-2xl bg-slate-50 p-4">
          <div>
            <p className="text-[11px] font-semibold uppercase text-slate-400">Range Revenue</p>
            <h4 className="text-lg font-bold text-slate-900">
              {compactInr(data?.totalRevenuePaise ?? 0)}
            </h4>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase text-slate-400">
              Today&apos;s Revenue
            </p>
            <h4 className="text-lg font-bold text-emerald-600">
              {compactInr(data?.todayRevenuePaise ?? 0)}
            </h4>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase text-slate-400">Products Sold</p>
            <h4 className="text-lg font-bold text-[#6C63FF]">{data?.products?.length ?? 0}</h4>
          </div>
        </div>

        {/* Product List */}
        <div className="mt-4 min-h-[250px] flex-1 overflow-y-auto pr-1">
          {isLoading ? (
            <div className="flex h-48 items-center justify-center">
              <Spinner size="md" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center text-slate-400">
              <Package className="h-10 w-10 stroke-1" />
              <p className="mt-2 text-sm font-medium">No sales recorded for this period</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredProducts.map((p) => {
                const pct = totalRev > 0 ? ((p.revenuePaise / totalRev) * 100).toFixed(1) : '0';
                return (
                  <div key={p.productId} className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      {p.imageUrl ? (
                        <img
                          src={p.imageUrl}
                          alt=""
                          className="h-10 w-10 rounded-xl border border-slate-100 object-cover"
                        />
                      ) : (
                        <div className="grid h-10 w-10 place-items-center rounded-xl bg-slate-100 text-slate-500">
                          <Package className="h-5 w-5" />
                        </div>
                      )}
                      <div>
                        <h4 className="text-sm font-bold text-slate-900">{p.title}</h4>
                        <p className="text-xs text-slate-500">
                          Seller:{' '}
                          <span className="font-semibold text-slate-700">{p.sellerName}</span> ·
                          Qty: <span className="font-semibold text-slate-700">{p.qty}</span>
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <h4 className="text-sm font-extrabold text-slate-900">
                        {formatPaise(p.revenuePaise)}
                      </h4>
                      <span className="text-[11px] font-bold text-emerald-600">
                        {pct}% contribution
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
